import React from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Table sizes
export type TableSize = 'small' | 'medium' | 'large';

// Table variants
export type TableVariant = 'default' | 'striped' | 'bordered';

// Column definition
export interface TableColumn<T = any> {
  /**
   * Unique key for the column
   */
  key: string;
  
  /**
   * Title of the column
   */
  title: React.ReactNode;
  
  /**
   * Width of the column
   */
  width?: string | number;
  
  /**
   * Render function for the column cell
   */
  render?: (value: any, record: T, index: number) => React.ReactNode;
  
  /**
   * Text alignment of the column
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
  
  /**
   * Whether the column is sortable
   * @default false
   */
  sortable?: boolean;
  
  /**
   * Whether the column is fixed
   * @default false
   */
  fixed?: boolean;
  
  /**
   * Whether the column is resizable
   * @default false
   */
  resizable?: boolean;
}

// Table props
export interface TableProps<T = any> {
  /**
   * The size of the table
   * @default 'medium'
   */
  size?: TableSize;
  
  /**
   * The variant of the table
   * @default 'default'
   */
  variant?: TableVariant;
  
  /**
   * The columns of the table
   */
  columns: TableColumn<T>[];
  
  /**
   * The data source of the table
   */
  dataSource: T[];
  
  /**
   * The key property of each record in the data source
   * @default 'key'
   */
  rowKey?: string | ((record: T) => string);
  
  /**
   * Whether the table has a header
   * @default true
   */
  showHeader?: boolean;
  
  /**
   * Whether the table is loading
   * @default false
   */
  loading?: boolean;
  
  /**
   * Whether the table is scrollable
   * @default false
   */
  scrollable?: boolean;
  
  /**
   * The maximum height of the table
   */
  maxHeight?: string | number;
  
  /**
   * Whether the table has zebra striping
   * @default false
   */
  striped?: boolean;
  
  /**
   * Whether the table has borders
   * @default false
   */
  bordered?: boolean;
  
  /**
   * Whether the table has hover effect
   * @default true
   */
  hoverable?: boolean;
  
  /**
   * Callback when a row is clicked
   */
  onRowClick?: (record: T, index: number, event: React.MouseEvent<HTMLTableRowElement>) => void;
  
  /**
   * Callback when a cell is clicked
   */
  onCellClick?: (record: T, column: TableColumn<T>, index: number, event: React.MouseEvent<HTMLTableCellElement>) => void;
  
  /**
   * Callback when a column header is clicked
   */
  onHeaderClick?: (column: TableColumn<T>, event: React.MouseEvent<HTMLTableCellElement>) => void;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Size styles
const getSizeStyles = (size: TableSize) => {
  switch (size) {
    case 'small':
      return css`
        font-size: ${typography.fontSize.sm};
        
        th, td {
          padding: ${spacingSystem.spacing[8]} ${spacingSystem.spacing[12]};
        }
      `;
    case 'medium':
      return css`
        font-size: ${typography.fontSize.md};
        
        th, td {
          padding: ${spacingSystem.spacing[12]} ${spacingSystem.spacing[16]};
        }
      `;
    case 'large':
      return css`
        font-size: ${typography.fontSize.lg};
        
        th, td {
          padding: ${spacingSystem.spacing[16]} ${spacingSystem.spacing[24]};
        }
      `;
    default:
      return '';
  }
};

// Variant styles
const getVariantStyles = (variant: TableVariant) => {
  switch (variant) {
    case 'default':
      return css`
        border-collapse: separate;
        border-spacing: 0;
        
        th {
          background-color: ${colors.neutral[100]};
          border-bottom: 1px solid ${colors.neutral[200]};
        }
        
        td {
          border-bottom: 1px solid ${colors.neutral[200]};
        }
      `;
    case 'striped':
      return css`
        border-collapse: separate;
        border-spacing: 0;
        
        th {
          background-color: ${colors.neutral[100]};
          border-bottom: 1px solid ${colors.neutral[200]};
        }
        
        td {
          border-bottom: 1px solid ${colors.neutral[200]};
        }
        
        tbody tr:nth-child(even) {
          background-color: ${colors.neutral[50]};
        }
      `;
    case 'bordered':
      return css`
        border-collapse: separate;
        border-spacing: 0;
        border: 1px solid ${colors.neutral[200]};
        
        th, td {
          border: 1px solid ${colors.neutral[200]};
        }
        
        th {
          background-color: ${colors.neutral[100]};
        }
      `;
    default:
      return '';
  }
};

// Styled table components
const TableContainer = styled.div<{ scrollable?: boolean; maxHeight?: string | number }>`
  width: 100%;
  overflow: auto;
  position: relative;
  
  ${(props: { scrollable?: boolean; maxHeight?: string | number }) => props.scrollable && css`
    overflow-y: auto;
    max-height: ${props.maxHeight || '400px'};
  `}
`;

const StyledTable = styled.table<{
  size: TableSize;
  variant: TableVariant;
  hoverable?: boolean;
}>`
  width: 100%;
  font-family: ${typography.fontFamily.primary};
  color: ${colors.neutral[900]};
  
  /* Size styles */
  ${(props: { size: TableSize }) => getSizeStyles(props.size)}
  
  /* Variant styles */
  ${(props: { variant: TableVariant }) => getVariantStyles(props.variant)}
  
  /* Hover effect */
  ${(props: { hoverable?: boolean }) => props.hoverable && css`
    tbody tr:hover {
      background-color: ${colors.primary[50]};
    }
  `}
  
  /* Cell styles */
  th, td {
    text-align: left;
    vertical-align: middle;
  }
  
  th {
    font-weight: ${typography.fontWeight.medium};
  }
`;

const TableHeader = styled.thead`
  /* Header styles */
`;

const TableBody = styled.tbody`
  /* Body styles */
`;

const TableRow = styled.tr<{ clickable?: boolean }>`
  /* Row styles */
  ${(props: { clickable?: boolean }) => props.clickable && css`
    cursor: pointer;
  `}
`;

const TableHeaderCell = styled.th<{ 
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
}>`
  /* Header cell styles */
  width: ${(props: { width?: string | number }) => props.width ? (typeof props.width === 'number' ? `${props.width}px` : props.width) : 'auto'};
  text-align: ${(props: { align?: 'left' | 'center' | 'right' }) => props.align || 'left'};
  
  ${(props: { sortable?: boolean }) => props.sortable && css`
    cursor: pointer;
    user-select: none;
    
    &:hover {
      background-color: ${colors.neutral[200]};
    }
  `}
`;

const TableCell = styled.td<{ 
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  clickable?: boolean;
}>`
  /* Cell styles */
  width: ${(props: { width?: string | number }) => props.width ? (typeof props.width === 'number' ? `${props.width}px` : props.width) : 'auto'};
  text-align: ${(props: { align?: 'left' | 'center' | 'right' }) => props.align || 'left'};
  
  ${(props: { clickable?: boolean }) => props.clickable && css`
    cursor: pointer;
    
    &:hover {
      background-color: ${colors.primary[100]};
    }
  `}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const EmptyState = styled.div`
  padding: ${spacingSystem.spacing[24]};
  text-align: center;
  color: ${colors.neutral[500]};
`;

/**
 * Table component for displaying data in a tabular format
 */
export const Table = <T extends Record<string, any> = any>({
  size = 'medium',
  variant = 'default',
  columns = [],
  dataSource = [],
  rowKey = 'key',
  showHeader = true,
  loading = false,
  scrollable = false,
  maxHeight,
  striped = false,
  bordered = false,
  hoverable = true,
  onRowClick,
  onCellClick,
  onHeaderClick,
  className,
}: TableProps<T>) => {
  // Determine the actual variant based on props
  const actualVariant = bordered ? 'bordered' : (striped ? 'striped' : variant);
  
  // Get row key
  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return record[rowKey] !== undefined ? record[rowKey] : `row-${index}`;
  };
  
  // Handle row click
  const handleRowClick = (record: T, index: number) => (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (onRowClick) {
      onRowClick(record, index, event);
    }
  };
  
  // Handle cell click
  const handleCellClick = (record: T, column: TableColumn<T>, index: number) => (event: React.MouseEvent<HTMLTableCellElement>) => {
    if (onCellClick) {
      onCellClick(record, column, index, event);
      // Prevent row click when cell is clicked
      event.stopPropagation();
    }
  };
  
  // Handle header click
  const handleHeaderClick = (column: TableColumn<T>) => (event: React.MouseEvent<HTMLTableCellElement>) => {
    if (onHeaderClick && column.sortable) {
      onHeaderClick(column, event);
    }
  };
  
  // Render table header
  const renderHeader = () => {
    if (!showHeader) {
      return null;
    }
    
    return (
      <TableHeader>
        <tr>
          {columns.map((column, index) => (
            <TableHeaderCell
              key={column.key || index}
              width={column.width}
              align={column.align}
              sortable={column.sortable}
              onClick={column.sortable ? handleHeaderClick(column) : undefined}
            >
              {column.title}
            </TableHeaderCell>
          ))}
        </tr>
      </TableHeader>
    );
  };
  
  // Render table body
  const renderBody = () => {
    if (dataSource.length === 0) {
      return (
        <TableBody>
          <tr>
            <td colSpan={columns.length}>
              <EmptyState>No data</EmptyState>
            </td>
          </tr>
        </TableBody>
      );
    }
    
    return (
      <TableBody>
        {dataSource.map((record, rowIndex) => (
          <TableRow
            key={getRowKey(record, rowIndex)}
            clickable={!!onRowClick}
            onClick={onRowClick ? handleRowClick(record, rowIndex) : undefined}
          >
            {columns.map((column, colIndex) => {
              const value = record[column.key];
              const cellContent = column.render ? column.render(value, record, rowIndex) : value;
              
              return (
                <TableCell
                  key={`${getRowKey(record, rowIndex)}-${column.key || colIndex}`}
                  width={column.width}
                  align={column.align}
                  clickable={!!onCellClick}
                  onClick={onCellClick ? handleCellClick(record, column, rowIndex) : undefined}
                >
                  {cellContent}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    );
  };
  
  // Render loading overlay
  const renderLoading = () => {
    if (!loading) {
      return null;
    }
    
    return (
      <LoadingOverlay>
        Loading...
      </LoadingOverlay>
    );
  };
  
  return (
    <TableContainer scrollable={scrollable} maxHeight={maxHeight} className={className}>
      {renderLoading()}
      <StyledTable size={size} variant={actualVariant} hoverable={hoverable}>
        {renderHeader()}
        {renderBody()}
      </StyledTable>
    </TableContainer>
  );
};

export default Table;